import {
  CANONICAL_ITEM_KINDS,
  ITEM_HOLDER_TYPES,
  ITEM_RUNTIME_ROW_TYPES,
  normalizeCanonicalItemKind,
  normalizeItemRulesSnapshot,
} from './contracts';
import { mapCraftCategoryToCanonicalKindAlias } from './categoryAliases';

const SHOP_CATEGORY_TO_KIND = Object.freeze({
  weapon: CANONICAL_ITEM_KINDS.weapon,
  armor: CANONICAL_ITEM_KINDS.armor,
  potion: CANONICAL_ITEM_KINDS.consumable,
  scroll: CANONICAL_ITEM_KINDS.consumable,
  food: CANONICAL_ITEM_KINDS.consumable,
  ingredient: CANONICAL_ITEM_KINDS.component,
  gear: CANONICAL_ITEM_KINDS.gear,
  currency: CANONICAL_ITEM_KINDS.currency,
  misc: CANONICAL_ITEM_KINDS.misc,
});

const LEGACY_INVENTORY_CATEGORY_TO_KIND = Object.freeze({
  weapon: CANONICAL_ITEM_KINDS.weapon,
  armor: CANONICAL_ITEM_KINDS.armor,
  combat: CANONICAL_ITEM_KINDS.combat,
  consumable: CANONICAL_ITEM_KINDS.consumable,
  component: CANONICAL_ITEM_KINDS.component,
  storage: CANONICAL_ITEM_KINDS.component,
  quest: CANONICAL_ITEM_KINDS.quest,
  currency: CANONICAL_ITEM_KINDS.currency,
  misc: CANONICAL_ITEM_KINDS.misc,
});

const cloneValue = (value) => {
  if (Array.isArray(value)) return value.map(cloneValue);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, innerValue]) => [key, cloneValue(innerValue)]),
    );
  }
  return value;
};

const toArray = (value) => (Array.isArray(value) ? value : []);

const normalizeNameKey = (value) => String(value || 'item')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '') || 'item';

const buildLegacyDefinitionId = (prefix, item) => {
  if (item?.itemId) return String(item.itemId);
  if (item?.definitionId) return String(item.definitionId);
  if (item?.id) return `${prefix}:${item.id}`;
  return `${prefix}:${normalizeNameKey(item?.name)}`;
};

const buildDefinitionShape = ({
  definitionId,
  kind,
  name,
  icon = null,
  rarity = null,
  sourceType,
  sourceCategory = null,
  tags = [],
  authoredRules = {},
  authoredText = {},
  authoredEconomy = {},
  sourceSnapshot = null,
}) => ({
  definitionId: String(definitionId),
  kind: normalizeCanonicalItemKind(kind),
  name: name || 'Item',
  icon,
  rarity,
  sourceType,
  sourceCategory,
  tags: cloneValue(tags),
  authoredRules: normalizeItemRulesSnapshot(authoredRules),
  authoredText: {
    shortDesc: authoredText.shortDesc || '',
    longDesc: authoredText.longDesc || '',
    notes: authoredText.notes || '',
    dmNotes: authoredText.dmNotes || '',
  },
  authoredEconomy: {
    basePrice: Number.isFinite(authoredEconomy.basePrice) ? authoredEconomy.basePrice : 0,
    tradeOnly: authoredEconomy.tradeOnly === true,
  },
  sourceSnapshot: cloneValue(sourceSnapshot),
});

export const mapShopCategoryToCanonicalKind = (category) => normalizeCanonicalItemKind(
  SHOP_CATEGORY_TO_KIND[category] || CANONICAL_ITEM_KINDS.misc,
);

export const mapLootCategoryToCanonicalKind = (category) => mapShopCategoryToCanonicalKind(category);

export const mapCraftCategoryToCanonicalKind = (category) => normalizeCanonicalItemKind(
  mapCraftCategoryToCanonicalKindAlias(category) || CANONICAL_ITEM_KINDS.misc,
);

export const mapLegacyInventoryCategoryToCanonicalKind = (category) => normalizeCanonicalItemKind(
  LEGACY_INVENTORY_CATEGORY_TO_KIND[category] || CANONICAL_ITEM_KINDS.misc,
);

const mapLegacyEntrySourceCategoryToCanonicalKind = (sourceCategory, inventoryCategory = null) => normalizeCanonicalItemKind(
  mapCraftCategoryToCanonicalKindAlias(sourceCategory) ||
  SHOP_CATEGORY_TO_KIND[String(sourceCategory || '').trim().toLowerCase()] ||
  LEGACY_INVENTORY_CATEGORY_TO_KIND[String(inventoryCategory || '').trim().toLowerCase()] ||
  CANONICAL_ITEM_KINDS.misc,
);

export const readShopTemplateAsItemDefinition = (template = {}) => buildDefinitionShape({
  definitionId: template.sourceDefinitionId || template.definitionId || template.itemId || template.id || `shop-template:${normalizeNameKey(template.name)}`,
  kind: mapShopCategoryToCanonicalKind(template.category),
  name: template.name,
  icon: template.iconRef || template.icon || null,
  rarity: template.rarity || null,
  sourceType: 'shop-template',
  sourceCategory: template.category || null,
  tags: toArray(template.tags),
  authoredRules: {
    buffs: toArray(template.buffs),
    debuffs: toArray(template.debuffs),
    onUseEffects: toArray(template.onUseEffects),
    combatBonuses: toArray(template.combatBonuses),
    abilities: toArray(template.abilities),
    usesTemplate: template.uses || null,
    buffsApplyOnUse: template.buffsApplyOnUse === true,
  },
  authoredText: {
    shortDesc: template.shortDesc || '',
    longDesc: template.longDesc || '',
    notes: template.notes || '',
    dmNotes: template.dmNotes || '',
  },
  authoredEconomy: {
    basePrice: template.basePrice,
    tradeOnly: template.tradeOnly === true,
  },
  sourceSnapshot: template,
});

export const readLootCatalogItemAsItemDefinition = (item = {}) => buildDefinitionShape({
  definitionId: item.definitionId || item.itemId || item.id || `loot-item:${normalizeNameKey(item.name)}`,
  kind: mapLootCategoryToCanonicalKind(item.category),
  name: item.name,
  icon: item.iconRef || item.icon || null,
  rarity: item.rarity || null,
  sourceType: 'loot-catalog',
  sourceCategory: item.category || null,
  tags: toArray(item.tags),
  authoredRules: {
    buffs: toArray(item.buffs),
    debuffs: toArray(item.debuffs),
    onUseEffects: toArray(item.onUseEffects),
    combatBonuses: toArray(item.combatBonuses),
    abilities: toArray(item.abilities),
    usesTemplate: item.uses || null,
    buffsApplyOnUse: item.buffsApplyOnUse === true,
  },
  authoredText: {
    shortDesc: item.shortDesc || '',
    longDesc: item.longDesc || '',
    notes: item.notes || '',
    dmNotes: item.dmNotes || '',
  },
  authoredEconomy: {
    basePrice: item.basePrice,
    tradeOnly: item.tradeOnly === true,
  },
  sourceSnapshot: item,
});

export const readCraftResultAsItemDefinition = (result = {}) => buildDefinitionShape({
  definitionId: result.id || `craft-result:${normalizeNameKey(result.name)}`,
  kind: mapCraftCategoryToCanonicalKind(result.cat),
  name: result.name,
  icon: result.icon || result._raw?.icon || null,
  rarity: result.rar || null,
  sourceType: 'craft-result',
  sourceCategory: result.cat || null,
  tags: toArray(result.tags || result._raw?.tags),
  authoredRules: {
    buffs: toArray(result.buffs),
    debuffs: toArray(result.debuffs),
    onUseEffects: toArray(result.onUse || result.onUseEffects),
    combatBonuses: toArray(result.combatBonuses),
    abilities: toArray(result.abilities),
    usesTemplate: result.uses || null,
    buffsApplyOnUse: mapCraftCategoryToCanonicalKind(result.cat) === CANONICAL_ITEM_KINDS.consumable,
  },
  authoredText: {
    shortDesc: result.desc || '',
    longDesc: result.longDesc || '',
    notes: result.notes || '',
    dmNotes: result.dmNotes || '',
  },
  authoredEconomy: {
    basePrice: result.price,
    tradeOnly: result.tradeOnly === true,
  },
  sourceSnapshot: result,
});

const buildDefinitionSnapshotFromLegacyInventoryItem = (item = {}) => {
  const sourceCategory = item._raw?.cat || item.provenance?.sourceCategory || item.category || null;
  return buildDefinitionShape({
    definitionId: buildLegacyDefinitionId('legacy-entry', item),
    kind: mapLegacyEntrySourceCategoryToCanonicalKind(sourceCategory, item.category),
    name: item.name,
    icon: item._raw?.icon || null,
    rarity: item._raw?.rar || null,
    sourceType: 'legacy-inventory',
    sourceCategory,
    tags: toArray(item._raw?.tags),
    authoredRules: {
      buffs: toArray(item.buffs),
      debuffs: toArray(item.debuffs),
      onUseEffects: toArray(item.onUseEffects),
      combatBonuses: toArray(item.combatBonuses),
      abilities: toArray(item.abilities),
      usesTemplate: item.uses || null,
      buffsApplyOnUse: item.buffsApplyOnUse === true,
    },
    authoredText: {
      shortDesc: '',
      longDesc: '',
      notes: item.notes || '',
      dmNotes: item.questDetails || '',
    },
    authoredEconomy: {
      basePrice: item.basePrice,
      tradeOnly: item.tradeOnly === true,
    },
    sourceSnapshot: item,
  });
};

export const readLegacyInventoryItemAsOwnedEntry = (
  item = {},
  { holderType = ITEM_HOLDER_TYPES.char, holderId = null, quantity = null } = {},
) => ({
  entryId: item.id || null,
  definitionId: buildLegacyDefinitionId('legacy-entry', item),
  kind: mapLegacyEntrySourceCategoryToCanonicalKind(
    item._raw?.cat || item.provenance?.sourceCategory || item.category,
    item.category,
  ),
  holderType,
  holderId,
  quantity: Math.max(1, quantity ?? item.quantity ?? 1),
  equipped: item.equipped === true,
  favorited: item.favorited === true,
  mutableState: {
    uses: cloneValue(item.uses || null),
    abilityState: cloneValue(item.abilities || []),
    removedItemBuffs: cloneValue(item.removedItemBuffs || {}),
  },
  provenance: {
    sourceType: item.lootSource ? 'loot-award' : (item.itemId ? 'definition' : 'legacy-custom'),
    sourceId: item.lootSource || item.itemId || null,
    acquiredAt: item.addedDate || null,
    transferredFrom: item.transferredFrom || null,
  },
  uiState: {
    notes: item.notes || '',
    questDetails: item.questDetails || '',
    questComplete: item.questComplete === true,
  },
  definitionSnapshot: buildDefinitionSnapshotFromLegacyInventoryItem(item),
  legacy: {
    inventoryCategory: item.category || null,
    itemId: item.itemId || null,
    raw: cloneValue(item),
  },
});

export const readPartyInventoryItemAsOwnedEntry = (item = {}, { holderId = null } = {}) => (
  readLegacyInventoryItemAsOwnedEntry(item, {
    holderType: ITEM_HOLDER_TYPES.party,
    holderId,
  })
);

export const readCraftingTableRowAsOwnedEntry = (
  row = {},
  { holderId = null } = {},
) => {
  const item = row.item || {};
  if (item._invSnapshot) {
    const tableRaw = {
      ...(item.icon ? { icon: item.icon } : {}),
      ...(item.cat ? { cat: item.cat } : {}),
    };
    const inventorySnapshot = Object.keys(tableRaw).length > 0
      ? {
          ...item._invSnapshot,
          _raw: {
            ...tableRaw,
            ...(item._invSnapshot._raw || {}),
          },
        }
      : item._invSnapshot;
    return readLegacyInventoryItemAsOwnedEntry(inventorySnapshot, {
      holderType: ITEM_HOLDER_TYPES.crafting,
      holderId,
      quantity: row.qty ?? item._invSnapshot.quantity ?? 1,
    });
  }

  const definitionSource = item._res || item;
  const definitionSnapshot = readCraftResultAsItemDefinition(definitionSource);
  return {
    entryId: item.id || null,
    definitionId: definitionSnapshot.definitionId,
    kind: definitionSnapshot.kind,
    holderType: ITEM_HOLDER_TYPES.crafting,
    holderId,
    quantity: Math.max(1, row.qty ?? 1),
    equipped: false,
    favorited: false,
    mutableState: {
      uses: cloneValue(definitionSource.uses || null),
      abilityState: cloneValue(definitionSource.abilities || []),
      removedItemBuffs: {},
    },
    provenance: {
      sourceType: 'craft-result',
      sourceId: definitionSource.id || item.id || null,
      acquiredAt: null,
      transferredFrom: null,
    },
    uiState: {
      notes: definitionSource.notes || '',
      questDetails: definitionSource.dmNotes || '',
      questComplete: false,
    },
    definitionSnapshot,
    legacy: {
      inventoryCategory: null,
      itemId: definitionSource.id || item.id || null,
      raw: cloneValue(row),
    },
  };
};

export const readShopInventoryRow = (row = {}, template = {}) => ({
  runtimeRowType: ITEM_RUNTIME_ROW_TYPES.shop,
  rowId: row.listingId || row.id || row.templateKey || row.itemId || template.id || `shop-row:${normalizeNameKey(template.name)}`,
  definitionId: template.sourceDefinitionId || template.definitionId || row.definitionId || row.itemId || template.itemId || template.id || `shop-template:${normalizeNameKey(template.name)}`,
  holderType: ITEM_HOLDER_TYPES.shop,
  holderId: row.shopId || null,
  price: Number.isFinite(row.price) ? row.price : 0,
  stock: row.stock ?? null,
  secrecy: row.secrecy || 'visible',
  tradeOnly: row.tradeOnly === true,
  definitionSnapshot: readShopTemplateAsItemDefinition(template),
  sourceSnapshot: cloneValue(row),
});

export const readLootCatalogRow = (item = {}, { revealedQty = null, dropId = null, encounterId = null } = {}) => {
  const snapshot = item.snapshot && typeof item.snapshot === 'object' ? item.snapshot : item;
  const definitionId = item.definitionId || item.itemId || snapshot.definitionId || snapshot.itemId || snapshot.id || `loot-item:${normalizeNameKey(snapshot.name || item.name)}`;
  const definitionSource = snapshot === item
    ? item
    : {
        ...snapshot,
        definitionId,
        itemId: snapshot.itemId || definitionId,
        id: snapshot.id || definitionId,
      };

  return {
    runtimeRowType: ITEM_RUNTIME_ROW_TYPES.loot,
    rowId: dropId || item.dropId || item.id || `loot-row:${normalizeNameKey(snapshot.name || item.name)}`,
    definitionId,
    holderType: ITEM_HOLDER_TYPES.loot,
    holderId: encounterId || item.encounterId || null,
    quantity: revealedQty ?? item.quantity ?? null,
    visibleToPlayers: item.visibleToPlayers === true,
    status: item.status || null,
    version: Number.isFinite(Number(item.version)) ? Number(item.version) : null,
    playerClaims: toArray(item.playerClaims),
    awardedTo: item.awardedTo || item.awardedToCharId || null,
    lootTableRowIndex: item.lootTableRowIndex ?? null,
    provenance: item.provenance ? cloneValue(item.provenance) : null,
    definitionSnapshot: readLootCatalogItemAsItemDefinition(definitionSource),
    sourceSnapshot: cloneValue(item),
  };
};
